import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { ethers, JsonRpcProvider } from 'ethers';
import { createPublicClient, http } from 'viem';
import { mainnet } from 'viem/chains';
import { SiweMessage } from 'siwe';

export const AuthContext = createContext();

export const AuthProvider = ({ children, chain = mainnet }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isENSHolder, setIsENSHolder] = useState(false);
  const [ensName, setEnsName] = useState(null);
  const [address, setAddress] = useState(null);
  const [bankSystemWallet, setBankSystemWallet] = useState(null);
  const [authStatus, setAuthStatus] = useState('idle'); // 'idle', 'connecting', 'signing', 'success', 'error'

  const publicClient = createPublicClient({
    chain,
    transport: http(`https://mainnet.infura.io/v3/40665c4b12ac4c47815efc0a1bc0d02f`),
    batch: { multicall: true },
  });

  // Small helpers for dedupe and session tracking
  const seenWalletSessions = useRef(new Set()); // track sessionId values we've already acted on
  const lastSeenMessage = useRef(new Map()); // simple message dedupe map
  const DE_DUP_MS = 1000; // ignore duplicate messages within this window

  // Listen for BankSystem Wallet connection and related messages
  useEffect(() => {
    const handleWalletMessage = (event) => {
      try {
        // Only trust messages from our wallet domain(s)
        if (!event.origin || !event.origin.includes('banksystem.co')) return;

        const data = event.data || {};
        // Log everything we accept for easier debugging
        console.log('🔗 AuthContext received wallet message:', data);

        // Message-level dedupe (messageId preferred, fallback to type+sessionId)
        const messageId = data.messageId || data.id || null;
        const sessionId = data.sessionId || null;
        const type = data.type || '';

        const dedupeKey = messageId || `${type}:${sessionId || 'no-session'}`;
        const now = Date.now();
        const last = lastSeenMessage.current.get(dedupeKey) || 0;
        if (now - last < DE_DUP_MS) {
          // Ignore near-duplicate messages
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[AuthContext] deduped wallet message:', dedupeKey);
          }
          return;
        }
        lastSeenMessage.current.set(dedupeKey, now);

        // Handle known message types
        switch (type) {
          case 'WALLET_APP_LOADED': {
            // Wallet informs us it's loaded and may include a sessionId.
            // We avoid re-running heavy logic on repeat loads by recording sessionId.
            if (sessionId) {
              if (seenWalletSessions.current.has(sessionId)) {
                // Already processed WALLET_APP_LOADED for this session; ignore.
                if (process.env.NODE_ENV !== 'production') {
                  console.debug('[AuthContext] already seen WALLET_APP_LOADED for session:', sessionId);
                }
                return;
              }
              // Mark sessionId seen so repeated WALLET_APP_LOADED messages are ignored.
              seenWalletSessions.current.add(sessionId);
            }

            // Request a one-time status snapshot from the wallet to avoid reacting to repeated loads.
            // Wallet should reply with WALLET_STATUS_RESPONSE.
            try {
              event.source?.postMessage?.({
                type: 'REQUEST_WALLET_STATUS',
                messageId: `req-${Date.now()}`,
                sessionId
              }, event.origin);
            } catch (err) {
              // ignore postMessage failures; we still continue gracefully
              console.warn('[AuthContext] failed to post REQUEST_WALLET_STATUS', err);
            }

            // Update minimal status so UI can show wallet presence
            setAuthStatus('wallet-loaded');
            return;
          }

          case 'WALLET_STATUS_RESPONSE': {
            // Wallet returns a snapshot of its current state.
            // We update non-destructive fields (address, bankSystemWallet) but we DO NOT auto-login here.
            // Auto-login remains tied to the explicit WALLET_UNLOCKED event to preserve your current flow.
            const { isConnected, account, ensName: reportedEns } = data;

            if (account) {
              setBankSystemWallet({
                address: account,
                connected: !!isConnected
              });
              setAddress(account);
              if (reportedEns) {
                setEnsName(reportedEns);
              }
            }

            // mark wallet ready state
            setAuthStatus('wallet-ready');
            return;
          }

          case 'WALLET_CONNECTED': {
            console.log('🏦 BankSystem Wallet connected:', data.address);
            setBankSystemWallet({
              address: data.address,
              connected: true
            });
            setAddress(data.address);
            setAuthStatus('connected');
            return;
          }

          case 'WALLET_CREATED': {
            console.log('🎉 New BankSystem Wallet created:', data.address);
            setBankSystemWallet({
              address: data.address,
              connected: true
            });
            setAddress(data.address);
            setAuthStatus('connected');
            return;
          }

          case 'WALLET_UNLOCKED': {
            console.log('🔓 BankSystem Wallet unlocked:', data.address);
            setBankSystemWallet({
              address: data.address,
              connected: true
            });
            setAddress(data.address);
            setAuthStatus('connected');
            // CRITICAL: Trigger the full login flow (preserve existing behavior)
            completeLogin(data.address);
            return;
          }

          case 'WALLET_APP_READY': {
            console.log('✅ Wallet app is ready and responsive');
            setAuthStatus('wallet-ready');
            return;
          }

          case 'SIGNATURE_RESPONSE':
          case 'SIGNATURE_RESULT':
          case 'SIGNATURE': {
            console.log('✅ Received signature from BankSystem Wallet');
            handleSignatureResponse(data);
            return;
          }

          default:
            console.log('📨 Unknown message type:', data.type);
            return;
        }
      } catch (err) {
        console.error('[AuthContext] wallet message handler error:', err);
      }
    };

    window.addEventListener('message', handleWalletMessage);
    return () => window.removeEventListener('message', handleWalletMessage);
    // Intentionally no dependencies: we want a single stable listener for the lifetime of the provider.
    // The functions called (completeLogin, handleSignatureResponse) are stable within this scope.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignatureResponse = async (data) => {
    try {
      setAuthStatus('verifying-signature');
      console.log('🔐 Processing signature response...');

      // Signature verification logic would go here
      // For now, we'll proceed to ENS check (completeLogin will handle it)
      await completeLogin(bankSystemWallet?.address || data.address);
    } catch (error) {
      console.error('❌ Signature verification failed:', error);
      setAuthStatus('error');
      alert('Signature verification failed: ' + (error?.message || String(error)));
    }
  };

  const validateSession = useCallback(async (addr, ens) => {
    try {
      console.log('🔍 Validating session for:', { addr, ens });
      if (!publicClient) {
        throw new Error('Public client not available');
      }

      const resolvedAddr = await publicClient.getEnsAddress({ name: ens });
      console.log('🔍 Resolved address for', ens, ':', resolvedAddr);

      if (!resolvedAddr || resolvedAddr.toLowerCase() !== addr.toLowerCase()) {
        throw new Error('ENS no longer points to this address');
      }

      const primaryName = await publicClient.getEnsName({ address: addr });
      console.log('🔍 Primary name for', addr, ':', primaryName);

      if (!primaryName || primaryName.toLowerCase() !== ens.toLowerCase()) {
        throw new Error('ENS is not set as primary name');
      }

      // Special check to allow 'web3broker.eth' for agent access
      if (ens.toLowerCase() === 'web3broker.eth') {
        setIsAuthenticated(true);
        setIsENSHolder(true);
        setEnsName(ens);
        setAddress(addr);
        setAuthStatus('success');
        console.log('✅ Gating opened for web3broker.eth');
        return;
      }

      setIsAuthenticated(true);
      setIsENSHolder(true);
      setEnsName(ens);
      setAddress(addr);
      setAuthStatus('success');
      console.log('✅ Session validated successfully');

    } catch (err) {
      console.error('❌ Session validation failed:', err.message);
      setAuthStatus('error');
      logout();
    }
  }, [publicClient]);

  // 🔥 FIXED: Auto-revalidation that was causing the flip-flop lockout
  useEffect(() => {
    const storedEns = localStorage.getItem('ensName');
    const storedAddress = localStorage.getItem('address');

    // Only validate if we have stored session AND we're not already authenticated
    if (storedEns && storedAddress && !isAuthenticated && !address) {
      console.log('🔍 Found stored session, validating...');
      validateSession(storedAddress, storedEns);
    }
  }, [validateSession, address, isAuthenticated]); // Added isAuthenticated to dependencies

  const openBankSystemWallet = (purpose = 'connect') => {
    console.log(`🏦 Opening BankSystem Wallet for: ${purpose}`);
    const walletWindow = window.open(
      'https://banksystem.co/banksystem/',
      'BankSystemWallet',
      'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
    );

    if (!walletWindow) {
      console.error('❌ Popup blocked! Please allow popups for BankSystem.');
      alert('Please allow popups for BankSystem to open the wallet app.');
      return null;
    }

    console.log('✅ Wallet app window opened successfully');
    return walletWindow;
  };

  const loginWithSIWE = async () => {
    try {
      console.log('🔐 ========== STARTING SIWE LOGIN ==========');
      setAuthStatus('connecting');

      // STEP 1: Check if we already have a connected wallet
      if (bankSystemWallet?.connected) {
        console.log('✅ Using existing connected wallet:', bankSystemWallet.address);
        await completeLogin(bankSystemWallet.address);
        return;
      }

      console.log('🔄 No existing wallet connection found');

      // STEP 2: Open wallet app for connection only (no SIWE yet)
      console.log('🏦 Opening wallet app for connection...');
      const walletWindow = openBankSystemWallet('connection');

      if (!walletWindow) {
        throw new Error('Could not open wallet app. Please allow popups.');
      }

      // STEP 3: Wait for wallet connection
      console.log('⏳ Waiting for wallet connection...');
      const connectionPromise = new Promise((resolve, reject) => {
        const connectionTimeout = setTimeout(() => {
          window.removeEventListener('message', handleConnection);
          reject(new Error('Wallet connection timeout. Please try again.'));
        }, 45000); // 45 seconds for connection

        const handleConnection = (event) => {
          if (event.origin.includes('banksystem.co') &&
              (event.data.type === 'WALLET_CONNECTED' || event.data.type === 'WALLET_CREATED' || event.data.type === 'WALLET_UNLOCKED')) {
            clearTimeout(connectionTimeout);
            window.removeEventListener('message', handleConnection);
            console.log('✅ Wallet connected successfully:', event.data.address);
            resolve(event.data.address);
          }
        };

        window.addEventListener('message', handleConnection);
      });

      const connectedAddress = await connectionPromise;
      console.log('✅ Wallet connection completed:', connectedAddress);

      // STEP 4: Close wallet window and proceed to ENS check
      if (walletWindow && !walletWindow.closed) {
        walletWindow.close();
        console.log('✅ Wallet window closed');
      }

      // STEP 5: Complete login with the connected address
      await completeLogin(connectedAddress);

    } catch (err) {
      console.error('❌ SIWE login failed:', err);
      setAuthStatus('error');
      alert(`Login failed: ${err.message}`);
      logout();
    }
  };

  const completeLogin = async (account) => {
    try {
      console.log('🔐 ========== COMPLETING LOGIN ==========');
      console.log('🔍 Checking ENS for address:', account);
      setAuthStatus('checking-ens');

      // Prioritize ethers fallback with timeout for viem
      let ens = null;
      try {
        console.log('🔍 Resolving ENS with viem...');
        const controller = new AbortController();
        setTimeout(() => controller.abort(), 10000);
        ens = await publicClient.getEnsName({ address: account }, { signal: controller.signal });
        console.log('✅ Viem ENS resolution:', ens);
      } catch (err) {
        console.warn('❌ Viem ENS resolution failed, trying ethers fallback:', err.message);
        const ethersProvider = new JsonRpcProvider('https://mainnet.infura.io/v3/40665c4b12ac4c47815efc0a1bc0d02f');
        ens = await ethersProvider.lookupAddress(account);
        console.log('✅ Ethers ENS resolution:', ens);
      }

      console.log('🔍 Final ENS result:', ens);

      if (!ens) {
        throw new Error('No ENS name found for this address. Please set a .xbsys.eth domain as your primary name.');
      }

      if (ens.toLowerCase() === 'web3broker.eth') {
        console.log('✅ Special access granted for web3broker.eth');
        localStorage.setItem('ensName', ens);
        localStorage.setItem('address', account);
        await validateSession(account, ens);
        return;
      }

      if (!ens.toLowerCase().endsWith('.xbsys.eth')) {
        throw new Error(`Primary ENS must be a .xbsys.eth subdomain. Your current ENS: ${ens || 'None'}`);
      }

      console.log('✅ Valid .xbsys.eth domain found:', ens);
      localStorage.setItem('ensName', ens);
      localStorage.setItem('address', account);
      await validateSession(account, ens);

    } catch (err) {
      console.error('❌ Login completion failed:', err.message);
      setAuthStatus('error');
      alert(`Login failed: ${err.message}`);
      logout();
    }
  };

  const logout = () => {
    console.log('🔐 Logging out...');
    localStorage.removeItem('ensName');
    localStorage.removeItem('address');
    setIsAuthenticated(false);
    setIsENSHolder(false);
    setEnsName(null);
    setAddress(null);
    setBankSystemWallet(null);
    setAuthStatus('idle');
    console.log('✅ Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isENSHolder,
      ensName,
      address,
      loginWithSIWE,
      logout,
      bankSystemWallet,
      authStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};
